import { createRouter, createWebHistory } from 'vue-router'
import store from '@/store'

// Import views
import LoginView from '@/views/LoginView.vue'
import RegisterView from '@/views/RegisterView.vue'
import ProfileView from '@/views/ProfileView.vue'
import AdminDashboard from '@/views/AdminDashboard.vue'
import TeacherDashboard from '@/views/TeacherDashboard.vue'
import StudentDashboard from '@/views/StudentDashboard.vue'
import UserManagement from '@/views/admin/UserManagement.vue'
import CsvImportView from '@/views/admin/CsvImportView.vue'
import SchoolSubjectsView from '@/views/admin/SchoolSubjectsView.vue'
import GroupsList from '@/components/groups/GroupsList.vue'
import GroupDetails from '@/components/groups/GroupDetails.vue'
import MainLayout from '@/layouts/AdminLayout.vue'
import QuestionList from '@/components/questions/QuestionList.vue'
import QuestionForm from '@/components/questions/QuestionForm.vue'
import QuestionDetail from '@/components/questions/QuestionDetail.vue'
import AnswerQuestion from '@/components/questions/AnswerQuestion.vue'
import PinsView from '@/views/pins/PinsView.vue'
import PinAccessView from '@/views/pins/PinAccessView.vue'
import EvaluationView from '@/views/evaluation/EvaluationView.vue'
import WaitingRoomView from '@/views/WaitingRoomView.vue'
import SessionDashboard from '@/views/teacher/SessionDashboard.vue'

const routes = [
  {
    path: '/',
    redirect: '/login',
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: { requiresGuest: true },
  },
  {
    path: '/dashboard',
    component: MainLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: AdminDashboard,
        beforeEnter: (to, from, next) => {
          const role = store.getters['auth/userRole']

          if (role === 'student') {
            next('/pin-access')
            return
          }

          if (role === 'admin' || role === 'teacher') {
            next()
          } else {
            const dashboardRoute = store.getters['auth/getDashboardRoute']
            next(dashboardRoute)
          }
        },
      },
    ],
  },
  {
    path: '/admin',
    component: MainLayout,
    meta: { requiresAuth: true, requiresRole: 'admin' },
    children: [
      {
        path: '',
        redirect: '/dashboard',
      },
      {
        path: 'users',
        name: 'UserManagement',
        component: UserManagement,
      },
      {
        path: 'users/import',
        name: 'CsvImport',
        component: CsvImportView,
      },
      {
        path: 'school-subjects',
        name: 'SchoolSubjects',
        component: SchoolSubjectsView,
      },
    ],
  },
  {
    path: '/questions',
    component: MainLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Questions',
        component: QuestionList,
        meta: { requiresRole: ['admin', 'teacher'] },
      },
      {
        path: 'create',
        name: 'CreateQuestion',
        component: QuestionForm,
        meta: { requiresRole: ['admin', 'teacher'] },
      },
      {
        path: ':id',
        name: 'QuestionDetail',
        component: QuestionDetail,
        props: (route) => ({ questionId: route.params.id }),
      },
      {
        path: ':id/edit',
        name: 'EditQuestion',
        component: QuestionForm,
        props: (route) => ({ questionId: route.params.id }),
        meta: { requiresRole: ['admin', 'teacher'] },
      },
      {
        path: ':id/answer',
        name: 'AnswerQuestion',
        component: AnswerQuestion,
        props: (route) => ({ questionId: route.params.id }),
        meta: { requiresRole: 'student' },
      },
    ],
  },
  {
    path: '/groups',
    component: MainLayout,
    meta: { requiresAuth: true, requiresRole: ['admin'] },
    children: [
      {
        path: '',
        name: 'Groups',
        component: GroupsList,
      },
      {
        path: ':id',
        name: 'GroupDetails',
        component: GroupDetails,
        props: true,
      },
    ],
  },
  {
    path: '/reports',
    component: MainLayout,
    meta: { requiresAuth: true, requiresRole: ['admin', 'teacher'] },
    children: [
      {
        path: '',
        name: 'Reports',
        component: () => import('@/views/ReportsView.vue'),
      },
    ],
  },
  {
    path: '/profile',
    component: MainLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Profile',
        component: ProfileView,
      },
    ],
  },
  {
    path: '/pins',
    component: MainLayout,
    meta: { requiresAuth: true, requiresRole: ['admin', 'teacher'] },
    children: [
      {
        path: '',
        name: 'PinsManagement',
        component: PinsView,
      },
    ],
  },
  {
    path: '/pin-access',
    component: MainLayout,
    meta: { requiresAuth: true, requiresRole: 'student' },
    children: [
      {
        path: '',
        name: 'PinAccess',
        component: PinAccessView,
      },
    ],
  },
  {
    path: '/evaluation/:groupId',
    component: MainLayout,
    meta: { requiresAuth: true, requiresRole: 'student', requiresPinAccess: true },
    children: [
      {
        path: '',
        name: 'Evaluation',
        component: EvaluationView,
      },
    ],
  },
  {
    path: '/waiting-room',
    component: MainLayout,
    meta: { requiresAuth: true, requiresRole: 'student', requiresSessionAccess: true },
    children: [
      {
        path: '',
        name: 'WaitingRoom',
        component: WaitingRoomView,
      },
    ],
  },
  {
    path: '/session/:pinCode',
    component: MainLayout,
    meta: { requiresAuth: true, requiresRole: ['admin', 'teacher'] },
    children: [
      {
        path: '',
        name: 'SessionDashboard',
        component: SessionDashboard,
      },
    ],
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  // Initialize auth state from localStorage
  if (!store.state.auth.user && store.state.auth.accessToken) {
    store.dispatch('auth/initializeAuth')
  }

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  const requiresGuest = to.matched.some((record) => record.meta.requiresGuest)
  const requiresRole = to.meta.requiresRole
  const requiresPinAccess = to.meta.requiresPinAccess
  const requiresSessionAccess = to.meta.requiresSessionAccess

  // Check if route requires authentication
  if (requiresAuth) {
    if (!store.getters['auth/isLoggedIn']) {
      next('/login')
      return
    }

    // Try to refresh token and fetch profile for ALL authenticated routes
    if (store.state.auth.accessToken) {
      try {
        // Fetch fresh profile to ensure user data is current
        await store.dispatch('auth/fetchProfile')
      } catch (error) {
        // If profile fetch fails, try to refresh token
        const refreshed = await store.dispatch('auth/refreshAccessToken')
        if (!refreshed) {
          next('/login')
          return
        }
      }
    }

    // Check role-based access
    if (requiresRole) {
      const userRole = store.getters['auth/userRole']
      const allowedRoles = Array.isArray(requiresRole) ? requiresRole : [requiresRole]

      if (!allowedRoles.includes(userRole)) {
        // Redirect to appropriate dashboard based on user's role
        const dashboardRoute = store.getters['auth/getDashboardRoute']
        next(dashboardRoute)
        return
      }
    }

    // Check PIN access requirement
    if (requiresPinAccess) {
      const accessData = store.getters['pins/getAccessData']
      const sessionStatus = store.getters['session/getSessionStatus']
      const sessionGroupId = store.getters['session/getGroupId']

      console.log('PIN Access Check:', {
        hasAccessData: !!accessData,
        accessDataGroupId: accessData?.group_id,
        sessionStatus,
        sessionGroupId,
        routeGroupId: to.params.groupId,
      })

      // If session is active, allow access even without pins/accessData
      if (sessionStatus === 'active' && sessionGroupId) {
        // Verify the groupId in route matches session groupId
        if (to.params.groupId && sessionGroupId != to.params.groupId) {
          console.log(
            'Route groupId mismatch with session groupId, redirecting to correct evaluation',
          )
          next({
            name: 'Evaluation',
            params: { groupId: sessionGroupId },
          })
          return
        }

        // If no groupId in route but session is active, redirect to correct evaluation
        if (!to.params.groupId) {
          console.log('No groupId in route but session active, redirecting with session groupId')
          next({
            name: 'Evaluation',
            params: { groupId: sessionGroupId },
          })
          return
        }

        // Session is active and groupIds match, allow access
        console.log('Session active, allowing access to evaluation')
        next()
        return
      }

      // If no session is active, require pins/accessData
      if (!accessData || !accessData.group_id) {
        console.log('No valid access data and session not active, redirecting to pin-access')
        next('/pin-access')
        return
      }

      // Verify the groupId in route matches access data
      if (to.params.groupId && accessData.group_id != to.params.groupId) {
        console.log('Route groupId mismatch with access data, redirecting to pin-access')
        next('/pin-access')
        return
      }

      console.log('PIN access granted')
    }

    // Check session access requirement (for waiting room)
    if (requiresSessionAccess) {
      const sessionStatus = store.getters['session/getSessionStatus']
      const pinCode = store.getters['session/getPinCode']

      if (!pinCode || !sessionStatus) {
        // Redirect to PIN access page if no valid session data
        next('/pin-access')
        return
      }

      // If session is already active, redirect to evaluation
      if (sessionStatus === 'active') {
        // Try to get group_id from session store first
        let groupId = store.getters['session/getGroupId']

        // Fallback to pins access data
        if (!groupId) {
          const sessionData = store.getters['pins/getAccessData']
          groupId = sessionData?.group_id
        }

        if (groupId) {
          console.log('Session already active, redirecting to evaluation:', groupId)
          next({
            name: 'Evaluation',
            params: { groupId },
          })
          return
        } else {
          console.error('Session active but no groupId available')
          next('/pin-access')
          return
        }
      }

      // If session is completed, redirect to pin access
      if (sessionStatus === 'completed') {
        next('/pin-access')
        return
      }
    }
  }

  // Check if route requires guest (not authenticated)
  if (requiresGuest && store.getters['auth/isLoggedIn']) {
    const dashboardRoute = store.getters['auth/getDashboardRoute']
    next(dashboardRoute)
    return
  }

  next()
})

export default router
